import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CalendlyButton } from '../components/CalendlyButton';
import { 
  ClipboardCheck, 
  Search, 
  FileText, 
  Users, 
  Target,
  ArrowRight,
  CheckCircle2,
  Building2,
  GraduationCap,
  Briefcase,
  MapPin
} from 'lucide-react';

const AuditQualite = () => {
  return (
    <>
      <Helmet>
        <title>Auditeur Qualité dans le Vaucluse | Mehdi Richa</title>
        <meta 
          name="description" 
          content="Auditeur qualité indépendant dans le Vaucluse. Audits internes, diagnostics qualité, préparation aux certifications. Consultant qualité Avignon, Carpentras, Orange." 
        />
        <meta property="og:title" content="Auditeur Qualité Vaucluse - Mehdi Richa" />
        <meta property="og:description" content="Consultant qualité dans le Vaucluse. Audits internes, préparation certifications, diagnostics. Interventions à Avignon et dans tout le Vaucluse." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/audit-qualite-vaucluse`} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-50 overflow-hidden" data-testid="audit-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-slate-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                Audit & Conseil Qualité
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
                Auditeur qualité dans le Vaucluse
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                En tant qu'<strong>auditeur qualité Vaucluse</strong>, je vous apporte un regard externe, 
                neutre et constructif sur vos pratiques. Mon objectif : vous aider à identifier vos 
                forces, vos axes d'amélioration et à préparer sereinement vos échéances.
              </p>
              <Button 
                asChild 
                size="lg"
                className="rounded-full bg-blue-600 hover:bg-blue-700 px-8 py-6 text-base"
                data-testid="audit-hero-cta"
              >
                <a href="mailto:mehdi.richa.formation@gmail.com?subject=Projet d'audit qualité">
                  Parler de votre projet d'audit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
            <div className="animate-fade-in animation-delay-200 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-blue-600/10 rounded-3xl transform rotate-3" />
                <img
                  src="https://images.unsplash.com/photo-1564846824194-346b7871b855?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzJTIwd3JpdGluZyUyMG9uJTIwcGFwZXIlMjBjbG9zZSUyMHVwJTIwYXVkaXQlMjBjaGVja2xpc3R8ZW58MHx8fHwxNzY5OTQ3NzMzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Audit qualité et checklist"
                  className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why External Auditor */}
      <section className="py-20 md:py-28 bg-white" data-testid="audit-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Pourquoi faire appel à un auditeur externe ?
            </h2>
            <p className="text-slate-600">
              Un consultant qualité Vaucluse apporte un œil neuf et objectif sur votre organisation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-100" data-testid="benefit-1">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Search className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Regard objectif</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Identification neutre des forces et faiblesses de votre système qualité
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-200" data-testid="benefit-2">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Préparation sereine</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Anticipez vos audits de certification grâce à un audit blanc réaliste
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-300" data-testid="benefit-3">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Sécurisation</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Vérifiez la conformité de vos pratiques avant tout contrôle externe
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-400" data-testid="benefit-4">
              <CardContent className="p-6">
                <div className="w-12 h-12 mb-4 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Image renforcée</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Montrez à vos clients et usagers votre engagement qualité
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="audit-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Mes interventions en audit qualité
            </h2>
            <p className="text-slate-600">
              Des prestations adaptées à vos besoins et à votre maturité en matière de qualité
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white border-0 shadow-sm hover:shadow-lg transition-shadow duration-300 animate-fade-in animation-delay-100" data-testid="service-audit-interne">
              <CardContent className="p-8">
                <div className="w-14 h-14 mb-6 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <ClipboardCheck className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">Audit interne</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Évaluation complète de votre système qualité selon vos référentiels internes 
                  ou les normes applicables. Identification des écarts et recommandations concrètes.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Analyse des processus clés
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Entretiens terrain
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Rapport détaillé et plan d'actions
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-lg transition-shadow duration-300 animate-fade-in animation-delay-200" data-testid="service-audit-blanc">
              <CardContent className="p-8">
                <div className="w-14 h-14 mb-6 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">Pré-audit / Audit blanc</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Simulation d'un audit de certification pour vous préparer sereinement. 
                  Conditions réelles pour identifier les derniers points à corriger.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Conditions d'audit réalistes
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Identification des non-conformités potentielles
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Coaching de vos équipes
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-lg transition-shadow duration-300 animate-fade-in animation-delay-300" data-testid="service-diagnostic">
              <CardContent className="p-8">
                <div className="w-14 h-14 mb-6 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <Search className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">Diagnostic qualité</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  État des lieux de vos pratiques pour définir une feuille de route qualité. 
                  Idéal pour les structures qui démarrent ou souhaitent se structurer.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Cartographie de l'existant
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Analyse des écarts vs objectifs
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Préconisations priorisées
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-lg transition-shadow duration-300 animate-fade-in animation-delay-400" data-testid="service-revue-doc">
              <CardContent className="p-8">
                <div className="w-14 h-14 mb-6 bg-blue-600 rounded-2xl flex items-center justify-center">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">Revue documentaire</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Analyse de votre documentation qualité : procédures, enregistrements, indicateurs. 
                  Vérification de la cohérence et de la conformité.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Audit documentaire complet
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Propositions de simplification
                  </li>
                  <li className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    Mise en conformité
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 md:py-28 bg-white" data-testid="audit-methodology">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Ma façon de travailler
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Mon approche d'<strong>auditeur qualité Avignon</strong> est résolument tournée vers le terrain. 
                Je privilégie les échanges directs avec vos équipes, l'observation des pratiques réelles, 
                et une restitution claire et pédagogique.
              </p>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Écoute et cadrage</h3>
                    <p className="text-slate-600 text-sm">Compréhension de votre contexte, vos enjeux, vos attentes</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Investigation terrain</h3>
                    <p className="text-slate-600 text-sm">Entretiens, observations, analyse documentaire sur site</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Restitution pédagogique</h3>
                    <p className="text-slate-600 text-sm">Rapport clair avec constats, analyses et recommandations actionnables</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Accompagnement</h3>
                    <p className="text-slate-600 text-sm">Suivi optionnel pour vous aider à mettre en œuvre les actions</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-3xl p-8 animate-fade-in animation-delay-200">
              <h3 className="font-serif text-xl font-bold text-slate-900 mb-6">Pour qui ?</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <Building2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900">PME / TPE</h4>
                    <p className="text-slate-600 text-sm">Structures souhaitant structurer ou certifier leur démarche qualité</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <GraduationCap className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Organismes de formation</h4>
                    <p className="text-slate-600 text-sm">Préparation et maintien de la certification Qualiopi</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <Briefcase className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Collectivités</h4>
                    <p className="text-slate-600 text-sm">Accompagnement des démarches qualité dans le secteur public</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-16 bg-slate-50" data-testid="audit-zone">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white rounded-2xl p-8 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Zone d'intervention</h3>
                <p className="text-slate-600">Auditeur qualité Avignon et tout le Vaucluse (Carpentras, Orange, Cavaillon...)</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {['Avignon', 'Carpentras', 'Orange', 'Cavaillon'].map((city) => (
                <span 
                  key={city}
                  className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-700"
                >
                  {city}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-slate-900" data-testid="audit-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-6 animate-fade-in">
            Un projet d'audit qualité ?
          </h2>
          <p className="text-slate-300 text-lg mb-10 animate-fade-in animation-delay-100">
            Discutons ensemble de vos besoins. Je vous propose un premier échange gratuit 
            pour comprendre votre contexte et définir la meilleure approche.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animation-delay-200">
            <CalendlyButton 
              size="lg"
              className="rounded-full bg-white text-slate-900 hover:bg-slate-100 px-10 py-6 text-lg"
            >
              Prendre rendez-vous
            </CalendlyButton>
            <Button 
              asChild 
              variant="outline"
              size="lg"
              className="rounded-full border-white text-white hover:bg-white/10 px-10 py-6 text-lg"
              data-testid="audit-cta-button"
            >
              <a href="mailto:mehdi.richa.formation@gmail.com?subject=Projet d'audit qualité">
                M'envoyer un email
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default AuditQualite;
